import utilPb from "@/proto/asset/util_pb";
import managedNodePb from "@/proto/asset/managedNode_pb";
import snapshotPb from "@/proto/asset/snapshot_pb";
import assetManagementPb from "@/proto/asset-mgmt/assetManagement_pb";
import vnfPb from "@/proto/vnf/vnf_pb";
import vtmPb from "@/proto/vtm/vtm_pb";
import mstMessage from "@/proto/master/message_pb";
import jointMessage from "@/proto/joint/message_pb";

import { DateTime } from "luxon";

export type ApiState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
};

export type PageState = {
  curPage: number;
  sortBy: string | null;
  descending: boolean | null;
  dataType?: string | null;
};

export type ManagedNodeWithZabbixStatus = {
  managedNode: managedNodePb.ManagedNode.AsObject;
  status: boolean;
  statusConfirmed: boolean;
  userInfo: vnfPb.UserInfo.AsObject | undefined;
};

export type TeleworkManagerNode = {
  managedNode: managedNodePb.ManagedNode.AsObject;
  networkId: string;
  networkName: string;
  userInfo: vnfPb.UserInfo.AsObject;
  onlineStatus: string;
  session: vtmPb.VPNSession.AsObject | undefined;
  name: string;
  department: string;
  vpnAccount: string;
  approved: boolean;
  location: string;
  toggle?: number;
};

export type TeleworkManagerSnapshot = {
  snapshot: snapshotPb.Snapshot.AsObject | undefined;
  networkName: string;
};

export type VPNSession = {
  logon: boolean;
  ipAddress: string;
  vpnAccount: string;
  loginTimestamp: number;
  logoutTimestamp: number;
  protocol: string;
  srcIpAddress: string;
  time: string;
  sent: number;
  rcvd: number;
  connTime: string;
  connMin: number;
};

export type StartupCount = {
  datetime: number; // epoch sec
  count: number;
};

export type DailyStartupCount = {
  date: string; // yyyymmdd
  totalCount: number;
};

export type TrafficVolume = {
  datetime: number; // epoch sec
  volume: number;
};

export type DailyTrafficVolume = {
  date: string; // yyyymmdd
  maxVolume: number;
};

export type AppCount = {
  appName: string;
  count: number;
};

export type NodePrivilege = {
  boardId: string;
  networkId: string;
  managedNodeId: string;
  managedNodeName: string;
  name: string;
  department: string;
  allowed: boolean;
};

export type AmbiguousNode = {
  managedNode: managedNodePb.ManagedNode.AsObject;
  networkId: string;
  networkName: string;
  allowed: boolean;
  checked: boolean;
};

export type Employee = {
  employeeId: string;
  customerId: number;
  employeeKey: string;
  employeeName: string;
  department: string;
  mailAddress: string;
  telephone: string;
};

export type EmployeeMapping = {
  masterDataType: string;
  propertyEmployeeKey: string;
  propertyEmployeeName: string;
  propertyDepartment: string;
  propertyMailAddress: string;
  propertyTelephone: string;
};

export type UploadResult = {
  action: string;
  employeeBefore?: Employee;
  employeeAfter?: Employee;
  success: boolean;
  errorMessage: string;
};

export type EmployeeInfo = {
  employeeId: string;
  customerId: number;
  employeeKey: string;
  employeeName: string;
  department: string;
  mailAddress: string;
  telephone: string;
  onlineStatus: string;
  securityStatus: string;
  windowsUpdateStatus: string;
  numberOfTerminals: number;
};

export type EmployeeAccount = {
  vpnAccounts: string[];
};

export type Terminal = {
  terminalId: string;
  terminalName: string;
  networkId: string;
  networkName: string;
  numberOfPackages: number;
  numberOfWindowsUpdates: number;
  system?: {
    os: string;
    version: string;
    buildNumber: string;
    serialNumber: string;
  };
};

export type TerminalInfo = {
  terminal: Terminal;
  onlineStatus: string;
  location: string;
  approved: boolean;
  windowsUpdateStatus: string;
};

export type VPNConnection = {
  logon: boolean;
  ipAddress: string;
  vpnAccount: string;
  loginTimestamp: number;
  logoutTimestamp: number;
  protocol: string;
  srcIpAddress: string;
  connTime: string;
  connMin: number;
};

export type TerminalAndEmployee = {
  terminal: Terminal;
  employee?: Employee;
  employeeNameAlt: string;
  departmentAlt: string;
};

export type TerminalAndEmployeeUpdate = {
  terminalAndEmployee: TerminalAndEmployee;
  selectable: boolean;
  selected: boolean;
};

export type EmployeePrivilege = mstMessage.EmployeePrivilege.AsObject;

export type ADServerSettings = {
  serverAddress: string;
  adUserId: string;
  adPassword: string;
};

export type ADServerInfo = {
  adServerId: string;
  vsrHostname: string;
  primary: boolean;
  displayName: string;
  settings: ADServerSettings;
  createdTimestamp: number;
  updatedTimestamp: number;
};

export const getErrorString = (err: any) => {
  return typeof err === "string" ? err : "Fatal Error";
};

export const getPaginationTotal = (
  data: { pagination?: utilPb.ResponsePagination.AsObject } | null,
) => {
  if (!data) return 0;
  if (!data.pagination) return 0;
  return data.pagination.total;
};

export const convertEpochToDatetime = (epoch: number | any): string => {
  return typeof epoch === "number"
    ? DateTime.fromSeconds(epoch).toFormat("yyyy-MM-dd HH:mm ZZ")
    : "N/A";
};

export const convertToEpoch = (text: string, fmt: string): number => {
  return DateTime.fromFormat(text, fmt).toSeconds();
};

export function verifyAssetCSV(csv: string): {
  assetList: assetManagementPb.Asset[];
  columns: assetManagementPb.AssetColumns | null;
  errors: string[];
} {
  const numBasics = 9;
  const numAdditionals = 20;
  const lines = csv.split(/\r\n|\r|\n/);
  const assetList: assetManagementPb.Asset[] = [];
  let columns: assetManagementPb.AssetColumns | null = null;
  const errors: string[] = [];

  // header
  const headers = lines[0].split(",");
  if (headers.length != numBasics + numAdditionals) {
    errors.push("ヘッダ行:項目数が正しくありません");
  } else {
    for (let i = 0; i < headers.length; i++) {
      headers[i] = headers[i].trim();
      if (headers[i].length == 0) {
        errors.push("ヘッダ行:項目" + (i + 1) + "が空です");
      }
    }
    if (errors.length == 0) {
      columns = new assetManagementPb.AssetColumns();
      columns.setColumnNameListList(headers.slice(numBasics));
    }
  }

  if (columns != null) {
    // body
    const mandatoryCols = [0];
    for (let iLine = 1; iLine < lines.length; iLine++) {
      if (lines[iLine].trim().length > 0) {
        const cols = lines[iLine].split(",");
        if (cols.length != numBasics + numAdditionals) {
          errors.push(iLine + 1 + "行目:項目数が正しくありません");
        } else {
          for (let iCol = 0; iCol < cols.length; iCol++) {
            cols[iCol] = cols[iCol].trim();
            if (mandatoryCols.includes(iCol) && cols[iCol].length == 0) {
              errors.push(
                iLine + 1 + "行目:" + (iCol + 1) + "列目は必須項目です",
              );
            }
          }
          const asset = new assetManagementPb.Asset();
          asset.setAssetId(cols[0]);
          asset.setHostname(cols[1]);
          asset.setAssetType(cols[2]);
          asset.setModelName(cols[3]);
          asset.setSerialNo(cols[4]);
          asset.setVendorName(cols[5]);
          asset.setUsername(cols[6]);
          asset.setDepartment(cols[7]);
          asset.setVpnAccount(cols[8]);
          asset.setExtraFieldListList(cols.slice(numBasics));
          assetList.push(asset);
        }
      }
    }
  }

  return {
    assetList: errors.length == 0 ? assetList : [],
    columns: errors.length == 0 ? columns : null,
    errors: errors,
  };
}

export function convertVPNSession(
  session: vtmPb.VPNSession.AsObject,
): VPNSession {
  let connMin = -1;
  if (session.loginTimestamp > 0 && session.logoutTimestamp > 0) {
    const login = Math.floor(session.loginTimestamp / 60);
    const logout = Math.floor(session.logoutTimestamp / 60);
    connMin = logout - login;
  }

  let connTime = "";
  if (connMin >= 0) {
    const minutes = connMin % 60;
    const hours = (connMin - minutes) / 60;
    if (hours == 0) {
      connTime = minutes + "m";
    } else {
      connTime = hours + "h " + (minutes < 10 ? "0" : "") + minutes + "m";
    }
  }

  return {
    logon: session.logon,
    ipAddress: session.ipAddress,
    vpnAccount: session.vpnAccount,
    loginTimestamp: session.loginTimestamp,
    logoutTimestamp: session.logoutTimestamp,
    protocol: session.protocol,
    srcIpAddress: session.srcIpAddress,
    time: session.time,
    sent: session.sent,
    rcvd: session.rcvd,
    connTime: connTime,
    connMin: connMin,
  };
}

export function convertEmployee(emp: mstMessage.Employee.AsObject): Employee {
  return {
    employeeId: emp.employeeId,
    customerId: emp.customerId,
    employeeKey: emp.employeeKey,
    employeeName: emp.employeeName,
    department: emp.department,
    mailAddress: emp.mailAddress,
    telephone: emp.telephone,
  };
}

export function convertTerminal(term: mstMessage.Terminal.AsObject): Terminal {
  const terminal: Terminal = {
    terminalId: term.terminalId,
    terminalName: term.terminalName,
    networkId: term.networkId,
    networkName: term.networkName,
    numberOfPackages: term.numberOfPackages,
    numberOfWindowsUpdates: term.numberOfWindowsUpdates,
  };
  if (term.system) {
    terminal.system = {
      os: term.system.os,
      version: term.system.version,
      buildNumber: term.system.buildNumber,
      serialNumber: term.system.serialNumber,
    };
  }
  return terminal;
}

export function convertVPNConnection(
  conn: mstMessage.VPNConnection.AsObject,
): VPNConnection {
  let connMin = -1;
  if (conn.loginTimestamp > 0 && conn.logoutTimestamp > 0) {
    const login = Math.floor(conn.loginTimestamp / 60);
    const logout = Math.floor(conn.logoutTimestamp / 60);
    connMin = logout - login;
  }

  let connTime = "";
  if (connMin >= 0) {
    const minutes = connMin % 60;
    const hours = (connMin - minutes) / 60;
    if (hours == 0) {
      connTime = minutes + "m";
    } else {
      connTime = hours + "h " + (minutes < 10 ? "0" : "") + minutes + "m";
    }
  }

  return {
    logon: conn.logon,
    ipAddress: conn.ipAddress,
    vpnAccount: conn.vpnAccount,
    loginTimestamp: conn.loginTimestamp,
    logoutTimestamp: conn.logoutTimestamp,
    protocol: conn.protocol,
    srcIpAddress: conn.srcIpAddress,
    connTime: connTime,
    connMin: connMin,
  };
}

export function convertEmployeeMapping(
  mapping: jointMessage.ADServerEmployeeMapping.AsObject,
): EmployeeMapping {
  return {
    masterDataType: "AD",
    propertyEmployeeKey: mapping.propertyEmployeeKey,
    propertyEmployeeName: mapping.propertyEmployeeName,
    propertyDepartment: mapping.propertyDepartment,
    propertyMailAddress: mapping.propertyMailAddress,
    propertyTelephone: mapping.propertyTelephone,
  };
}

const ipaddr = require("ipaddr.js");
export function isIpAddressInRange(ipAddress: string, range: string) {
  const addr = ipaddr.parse(ipAddress);
  if (ipaddr.isValidCIDR(range)) {
    return addr.match(ipaddr.parseCIDR(range));
  }
  if (ipaddr.isValid(range)) {
    return ipAddress == range;
  }
  return false;
}
